import * as React from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { fetchApi } from '../../services/api';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import LoadingPanel from '../LoadingPanel';
import {
    Grid,
    GridColumn as Column,
    GridPageChangeEvent,
    GridCell,
    GridToolbar,
    GridPagerSettings,
    GridSortChangeEvent
} from '@progress/kendo-react-grid';
import { StreetAddressViewModel } from '../StreetAddress';
import { APIInfo } from '../Invoices';
const DocumentTitle = require('react-document-title');

type Props = RouteComponentProps<{}>;

export interface AccountData {
    BillToName: string;
    AccountID: number;
    BillToEmail: string;
    BillToAddress: StreetAddressViewModel | null;
}

export type AlertData = {
    Message: string;
    Link: string;
}

type State = {
    accounts: AccountData[];
    isLoading: boolean;
    skip: number;
    take: number;
    pageSize: number;
    pageable: boolean | GridPagerSettings;
    sort: SortDescriptor[];
    errors: AlertData[];
    processInvoices: boolean;
    apiInfo: APIInfo;
}

const centerDiv = {
    margin: "auto",
    width: "50%",
    textAlign: "center",
    opacity: 0.4,
    fontSize: "14px",
    fontStyle: "italic",
    paddingTop: "15px"
} as React.CSSProperties;

class QBValidateEntities extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);

        this.state = {
            accounts: [],
            isLoading: false,
            skip: 0,
            take: 50,
            pageSize: 50,
            pageable: this.state ? this.state.pageable : {
                buttonCount: 10,
                info: true,
                type: 'numeric',
                pageSizes: [50, 100, 150],
                previousNext: true
            },
            sort: [],
            errors: [],
            processInvoices: false,
            apiInfo: null
        }
    }

    public componentWillMount() {
        const queryString = require('query-string');
        const parsed = queryString.parse(location.search);
        if (parsed && parsed.BillingPeriodId) {
            this.getAPIInfoAndValidateEntities(parsed.BillingPeriodId);
            // this.validateEntities(parsed.BillingPeriodId);
        }
    }

    private sortChange(event: GridSortChangeEvent) {
        this.setState({
            sort: event.sort,
            accounts: orderBy(this.state.accounts, event.sort)
        });
    }

    private pageChange(event: GridPageChangeEvent) {
        this.setState({
            skip: event.page.skip,
            take: event.page.take,
            pageSize: event.page.take
        });
    }

    private getAPIInfoAndValidateEntities(BillingPeriodId: number) {
        this.setState({ isLoading: true });
        fetchApi('/api/Billing/GetAPIInfo')
            .then((_data: APIInfo) => {                
                this.setState({
                    apiInfo: _data
                }, () => this.validateEntities(BillingPeriodId));
            }).catch(() => {
                alert("Can't connect to server");
            });
    }

    private validateEntities(BillingPeriodId: number) {
        const data = {
            BillingPeriodId: BillingPeriodId
        }        
        this.setState({ isLoading: true });
        var link = this.state.apiInfo.Links.filter((x: ApiLink) => x.Rel === 'ValidateEntities')[0].Href;
        //'/api/Billing/ValidateQBEntities'
        fetchApi(link, data, 'POST')
            .then((_data: { MissingAccounts: AccountData[] }) => {
                this.setState({ accounts: _data.MissingAccounts, isLoading: false });
            });
    }

    private importMissingAccounts() {
        const data = {
            ChildInvoices: this.state.accounts
        }
        this.setState({ isLoading: true });
        var link = this.state.apiInfo.Links.filter((x: ApiLink) => x.Rel === 'CreateAccounts')[0].Href;
        //'/api/Billing/CreateQBAccounts
        fetchApi(link, data, 'POST')
            .then((_data: { Errors: AlertData[] }) => {
                this.setState({ errors: _data.Errors, isLoading: false }, () => { if (this.state.errors.length == 0) this.setState({ processInvoices: true }, () => { self.close(); }) });
            });
    }

    public render() {
        if (!this.state.accounts.length) {
            return <React.Fragment>
                <div style={centerDiv}>Loading...</div>
            </React.Fragment>
        }

        let data: AccountData[] = this.state.accounts;
        return <React.Fragment>
            <DocumentTitle title="Validate Accounts" />
            {this.state.isLoading && <LoadingPanel />}
            <input type="hidden" id="processInvoices" value={String(this.state.processInvoices)} />
            {this.state.errors.map((error, index) => {
                return <div className="alert alert-danger d-print-none" key={index}>
                    {error.Message}&nbsp;
                    {error.Link && <Link to={error.Link} className="alert-link">Fix Here</Link>}
                </div>
            })}
            <br />
            <Grid
                style={{ maxHeight: "1000px", maxWidth: "90%", margin: "auto" }}
                skip={this.state.skip}
                take={this.state.take}
                total={data.length}
                pageable={this.state.pageable}
                pageSize={this.state.pageSize}
                onPageChange={this.pageChange}
                sortable={{ allowUnsort: true }}
                sort={this.state.sort}
                onSortChange={this.sortChange}
                data={data.slice(this.state.skip, this.state.take + this.state.skip)}
            >
                <GridToolbar>
                    {this.state.apiInfo && <label style={{ fontSize: "large" }}><b>{"Accounts Missing in" + this.state.apiInfo.Name}</b></label>}
                    {this.state.apiInfo && <button className="btn btn-primary float-right" onClick={() => { window.open(this.state.apiInfo.PortalURL); }}>{"Resolve Manually via " + this.state.apiInfo.Name}</button>}
                    <label className="float-right">&nbsp;</label>
                    <button className="btn btn-success float-right" onClick={() => { this.importMissingAccounts(); }}>Import Missing Accounts</button>
                </GridToolbar>
                <Column field="BillToName" title="Account Name" cell={AccountNameCell} />
                <Column field="BillToAddress" title="Address" cell={AccountAddressCell} />
            </Grid>
        </React.Fragment>;
    }
}

class AccountNameCell extends GridCell {
    render() {
        return (
            <td>
                {this.props.dataItem.BillToName}
            </td>
        );
    }
}

class AccountAddressCell extends GridCell {
    render() {
        return (
            <td>
                {this.props.dataItem.BillToAddress.Line1 + " "}
                {this.props.dataItem.BillToAddress.City + ", "}
                {this.props.dataItem.BillToAddress.State + " "}
                {this.props.dataItem.BillToAddress.Zip + " "}
            </td>
        );
    }
}

export default QBValidateEntities;
